import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { todayLocal } from '../lib/dateHelpers';
import PdfStorageNotice from '../components/field-report/PdfStorageNotice';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import FileUpload from '../components/FileUpload';
import TimeInput from '../components/TimeInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, 
  Save, 
  Trash2, 
  Phone, 
  Mail, 
  MapPin,
  Loader2,
  ExternalLink,
  Copy,
  Plus,
  AlertTriangle,
  FileCheck,
  Ticket,
  X,
  Timer,
  DollarSign,
  Clock,
  Paperclip,
  Pencil,
  FolderOpen,
  Star
} from 'lucide-react';
import MapButtons from '../components/MapButtons';
import { toast } from 'sonner';
import { formatDate, getStatusColor, getStatusStyle, getPriorityColor, getContrastTextColor } from '../lib/utils';
import { getWeek, parseISO } from 'date-fns';
import { useJobLayoutSettings } from '../hooks/useJobLayoutSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prompt 33 — labels for the collapsed "Address history" panel (super users only)
const ADDRESS_FIELD_LABELS = {
  service_street: 'Street',
  service_city: 'City',
  service_state: 'State',
  service_zip: 'ZIP',
};

// Prompt 36 — how each creation source is labeled in the Address history panel
const ADDRESS_SOURCE_LABELS = {
  proposal_conversion: 'Converted from proposal',
  duplicate: 'Duplicated from another job',
  recurring_job: 'Recurring job series',
  new_job: 'New job',
  day_creation: '+ Add Day',
};

/**
 * Per-chip popover for changing a day's date. Each chip in the series
 * strip renders one of these — Radix manages open/close internally,
 * so multiple chips can't fight over a single `open` state. One tap
 * pops the editor open right beside the chip; no portal mount delay
 * (unlike the Dialog this replaced), so even first-click is instant.
 */
function DayChipPopover({ dayDoc, position, isActive, label, onNavigate, onAfterSave }) {
  const [open, setOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState('');
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) setPendingDate(dayDoc.date || '');
  };

  const handleSave = async () => {
    if (!pendingDate || !/^\d{4}-\d{2}-\d{2}$/.test(pendingDate)) {
      toast.error('Pick a valid date.');
      return;
    }
    if (pendingDate === dayDoc.date) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/jobs/${dayDoc.id}`, { date: pendingDate });
      toast.success(`Day ${position} moved to ${pendingDate}.`);
      setOpen(false);
      if (onAfterSave) await onAfterSave();
    } catch (e) {
      toast.error(`Failed to update date: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={isActive ? 'default' : 'outline'}
          size="sm"
          className={isActive
            ? 'bg-amber-500 hover:bg-amber-400 text-slate-900'
            : 'border-amber-300 text-amber-800 hover:bg-amber-100'}
          data-testid={`daily-series-jump-${position}`}
          title={`Tap to change Day ${position}\u2019s date`}
        >
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start" data-testid={`change-day-date-dialog-${position}`}>
        <div className="space-y-3">
          <div>
            <div className="font-semibold text-sm">Change Day {position}&rsquo;s date</div>
            <p className="text-xs text-slate-600 mt-0.5">Other days in this series stay where they are.</p>
          </div>
          <Input
            type="date"
            value={pendingDate}
            onChange={(e) => setPendingDate(e.target.value)}
            autoFocus
            data-testid={`change-day-date-input-${position}`}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !pendingDate || pendingDate === dayDoc.date}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              data-testid={`change-day-date-save-btn-${position}`}
            >
              {saving ? 'Saving…' : 'Save Date'}
            </Button>
          </div>
          {!isActive && (
            <button
              type="button"
              className="text-xs text-amber-700 hover:text-amber-900 underline w-full text-left pt-1 border-t"
              onClick={() => {
                setOpen(false);
                onNavigate();
              }}
              data-testid={`open-day-${position}-link`}
            >
              Open Day {position} (edit files, team, etc.) →
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, hasPermission, isSuperUser } = useAuth();
  const { canShowModule } = useJobLayoutSettings(user?.role);
  const isNew = id === 'new';
  // Day-Scoped Equipment Reservation System (Aug 2026): granular toggle,
  // default off — Admins/Super Users bypass via hasPermission automatically.
  const canAssignEquipment = hasPermission('assign_equipment_to_jobs');

  // Address-change audit log (Prompt 33, preview-only) — super users only.
  // Lazy-fetched the first time the collapsed "Address history" panel is
  // expanded, so it never adds a request to the normal page load.
  const [addressHistory, setAddressHistory] = useState([]);
  const [addressHistoryLoaded, setAddressHistoryLoaded] = useState(false);
  const [addressHistoryLoading, setAddressHistoryLoading] = useState(false);
  const fetchAddressHistory = useCallback(async () => {
    if (addressHistoryLoaded || isNew || !id) return;
    setAddressHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/jobs/${id}/address-history`);
      setAddressHistory(res.data || []);
      setAddressHistoryLoaded(true);
    } catch (err) {
      console.error('Failed to load address history:', err);
    } finally {
      setAddressHistoryLoading(false);
    }
  }, [addressHistoryLoaded, isNew, id]);
  
  // Check if user came from calendar - if so, navigate back to calendar on close
  const cameFromCalendar = location.state?.from === 'calendar';
  const cameFromDashboard = location.state?.from === 'dashboard';
  const backDestination = cameFromCalendar ? '/calendar' : cameFromDashboard ? '/' : '/jobs';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [services, setServices] = useState([]);
  const [proposalTypes, setProposalTypes] = useState([]);
  const [team, setTeam] = useState([]);
  // Feb-2026 Phase 3: raw asset rows + shop-only category set for the
  // "Assign Equipment" checkbox panel. Kept raw here (unfiltered by the
  // job's own assignment) so the memoized `equipmentList` below can
  // still show an already-assigned-but-now-Retired/Sold/In-Shop item
  // (so it can be released) while blocking it from NEW selection —
  // see Day-Scoped Equipment Reservation System (Aug 2026).
  const [assetRows, setAssetRows] = useState([]);
  const [shopOnlyCatSet, setShopOnlyCatSet] = useState(() => new Set());
  const BLOCKED_EQUIPMENT_STATUSES = useMemo(() => new Set(['Retired', 'Sold', 'In Shop', 'Checked Out']), []);
  // NOTE: `equipmentList` is defined AFTER the `formData` useState below —
  // defining it here would hit a temporal-dead-zone ReferenceError
  // ("Cannot access 'formData' before initialization") and crash the page.
  // Office team request: Assign Equipment accordion must open fully
  // collapsed by default, then persist whatever the user leaves it
  // as (mirrors the maintenance.filterType localStorage pattern) —
  // no per-job scoping since categories are the same fleet-wide list.
  const [assignEquipmentOpen, setAssignEquipmentOpen] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('jobDetail.assignEquipmentOpenCategories'));
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem('jobDetail.assignEquipmentOpenCategories', JSON.stringify(assignEquipmentOpen)); } catch {}
  }, [assignEquipmentOpen]);
  // Owner request (Aug 2026): the accordion above persists forever with
  // no per-job scoping, which can look like a mystery "stuck open"
  // default days later. Stamp WHEN each category was opened so we can
  // show "Opened by you • 2 days ago" instead of leaving it ambiguous.
  const [assignEquipmentOpenedAt, setAssignEquipmentOpenedAt] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('jobDetail.assignEquipmentOpenedAt'));
      return saved && typeof saved === 'object' ? saved : {};
    } catch { return {}; }
  });
  const handleAssignEquipmentOpenChange = (newOpen) => {
    setAssignEquipmentOpenedAt(prev => {
      const next = {};
      newOpen.forEach(key => { next[key] = prev[key] || new Date().toISOString(); });
      try { localStorage.setItem('jobDetail.assignEquipmentOpenedAt', JSON.stringify(next)); } catch {}
      return next;
    });
    setAssignEquipmentOpen(newOpen);
  };
  // Feb-2026 round 5: full unfiltered set of asset ids so we can
  // detect assigned equipment that has been deleted from the fleet
  // (owner asked: show a placeholder instead of silently dropping
  // it, so the historical record stays visible). Separate state
  // from `equipmentList` because that one is post-filter for the
  // picker; this needs the whole fleet. `assetsLoaded` guards
  // against a failed /api/assets call showing a false orphan notice
  // when the picker is simply empty (testing agent iter112 note #3).
  const [allAssetIds, setAllAssetIds] = useState(() => new Set());
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  // Feb-2026 round 4: Confined Space submitter gate on the Job page
  // Field Reports panel. Backed by the same /api/cse-restrictions
  // config doc used everywhere else (see Phase 4). Admin bypass —
  // matches how canSubmitCSE works on the Field Reports page itself.
  const [cseRestrictions, setCseRestrictions] = useState({ submitter_ids: [] });
  const canSubmitCSE = isAdmin() || (cseRestrictions.submitter_ids || []).includes(user?.id);
  const [contacts, setContacts] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyContacts, setSelectedCompanyContacts] = useState([]);
  const [selectedContactIdx, setSelectedContactIdx] = useState('');
  const [jobReports, setJobReports] = useState({ precons: [], softDigs: [], locates: [], cseForms: [] });
  const [jobStatuses, setJobStatuses] = useState([]);
  const [dailySeries, setDailySeries] = useState([]);
  const [addingDay, setAddingDay] = useState(false);
  const [showAddSeriesDayDialog, setShowAddSeriesDayDialog] = useState(false);
  const [addSeriesDate, setAddSeriesDate] = useState('');
  const [addSeriesTeam, setAddSeriesTeam] = useState([]);
  // Per-chip popovers each manage their own state (see DayChipPopover
  // above). The single-day case below uses a small inline popover too —
  // single tap, instant open, no portal lag.
  const [singleDayOpen, setSingleDayOpen] = useState(false);
  const [singleDayDate, setSingleDayDate] = useState('');
  const [singleDaySaving, setSingleDaySaving] = useState(false);

  const handleSaveSingleDayDate = async () => {
    if (!singleDayDate || !/^\d{4}-\d{2}-\d{2}$/.test(singleDayDate)) {
      toast.error('Pick a valid date.');
      return;
    }
    if (singleDayDate === formData.date) {
      setSingleDayOpen(false);
      return;
    }
    setSingleDaySaving(true);
    try {
      await axios.put(`${API}/jobs/${id}`, { date: singleDayDate });
      toast.success(`Date updated to ${singleDayDate}.`);
      setSingleDayOpen(false);
      await fetchJob();
    } catch (e) {
      toast.error(`Failed to update date: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSingleDaySaving(false);
    }
  };

  const toggleAddSeriesTeamMember = (memberId) => {
    setAddSeriesTeam((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );
  };

  const handleAddDayToSeries = async () => {
    if (!addSeriesDate || !/^\d{4}-\d{2}-\d{2}$/.test(addSeriesDate)) {
      toast.error('Please pick a valid date.');
      return;
    }
    setAddingDay(true);
    try {
      // Use the unified per-job endpoint — handles both single-day
      // auto-conversion and existing series extension transparently.
      const r = await axios.post(
        `${API}/jobs/${id}/add-day`,
        { date: addSeriesDate, assigned_team_members: addSeriesTeam },
      );
      toast.success(`Day added for ${addSeriesDate}. Opening it now…`);
      setShowAddSeriesDayDialog(false);
      navigate(`/jobs/${r.data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Failed to add day: ${detail}`);
    } finally {
      setAddingDay(false);
    }
  };

  const [formData, setFormData] = useState({
    client_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_name: '',
    phone: '',
    email: '',
    service_address: '',
    service_street: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    job_title: '',
    service_type: '',
    custom_service_type: '',
    date: todayLocal(),
    end_date: '',
    time: '',
    duration: '',
    estimated_hours: '',
    labor_cost_per_hour: '',
    priority: 'Normal',
    reminder: '',
    status: 'Pending',
    notes: '',
    coordination_notes: '',
    field_notes: '',
    site_plan_notes: '',
    is_recurring: false,
    recurrence_pattern: '',
    assigned_team_members: [],
    // Feb-2026 CLL-meeting Phase 3: equipment assigned to this job.
    // Purely a usage/tracking record — office handles costing elsewhere.
    // Populated by the "Assign Equipment" checkbox panel on this page.
    assigned_equipment_ids: [],
    team_lead_id: null,
    files: [],
    photos: [],
    job_number: '',
    drive_folder_url: '',
    tickets_811: [],
    permits: [],
    // Lineage tracking fields
    proposal_id: '',
    proposal_number: '',
    opportunity_id: '',
    opportunity_number: '',
    client_id: '',
    contact_id: '',
    line_items: [],
    total: null,
    // Multi-day job fields
    is_multi_day: false,
    job_days: []
  });

  // Day-Scoped Equipment Reservation System — selectable equipment list.
  // Must be declared after `formData` (see note above).
  const equipmentList = useMemo(() => {
    const assignedIds = new Set(formData.assigned_equipment_ids || []);
    return assetRows.filter(a =>
      a && a.name &&
      !shopOnlyCatSet.has(a.category) &&
      a.assignable_to_jobs !== false &&
      (!BLOCKED_EQUIPMENT_STATUSES.has(a.status) || assignedIds.has(a.id))
    );
  }, [assetRows, shopOnlyCatSet, formData.assigned_equipment_ids, BLOCKED_EQUIPMENT_STATUSES]);

  // Multi-day job state
  const [showAddDayDialog, setShowAddDayDialog] = useState(false);
  const [newDayData, setNewDayData] = useState({
    date: '',
    time: '',
    assigned_to: [],
    notes: '',
    service_type: '',
    assigned_line_items: [],
    day_title: ''
  });
  const [editingDay, setEditingDay] = useState(null);
  const [pendingDays, setPendingDays] = useState([]); // For days added during job creation

  const fetchJob = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      const data = response.data || {};
      // Migrate legacy single Notes → Coordination Notes (one-time, on first open)
      // Only when neither new field has been populated yet.
      const hasNewNotes = (data.coordination_notes && data.coordination_notes.trim()) ||
                          (data.field_notes && data.field_notes.trim());
      if (!hasNewNotes && data.notes && data.notes.trim()) {
        data.coordination_notes = data.notes;
      }
      setFormData(data);
      // If this job is part of a daily series, fetch the sibling days for quick-jump
      if (response.data?.daily_series_id) {
        try {
          const seriesRes = await axios.get(`${API}/jobs/series/${response.data.daily_series_id}`);
          setDailySeries(seriesRes.data || []);
        } catch (e) {
          console.error('Failed to fetch daily series:', e);
          setDailySeries([]);
        }
      } else {
        setDailySeries([]);
      }
    } catch (error) {
      toast.error('Failed to load job');
      navigate(backDestination);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, backDestination]);

  // After uploading/deleting an attachment we MUST NOT replace the whole form
  // state — that would wipe out any in-progress text edits the user typed
  // before clicking upload. Only refresh the attachment arrays from the
  // server and merge them into local state, leaving everything else alone.
  const refreshAttachments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      const data = response.data || {};
      setFormData(prev => ({
        ...prev,
        files: data.files || [],
        photos: data.photos || [],
        tickets_811_files: data.tickets_811_files || [],
        permit_files: data.permit_files || [],
      }));
    } catch (error) {
      console.error('Failed to refresh attachments:', error);
    }
  }, [id]);

  const fetchReferenceData = async () => {
    try {
      const response = await axios.get(`${API}/reference-data`);
      const data = response.data;
      setServices(data.services || []);
      setTeam(data.team || []);
      setContacts(data.contacts || []);
      setJobStatuses((data.job_statuses || []).filter(s => s.active));
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
    try {
      const ptRes = await axios.get(`${API}/proposal-types`);
      setProposalTypes(ptRes.data || []);
    } catch (error) {
      console.error('Failed to fetch proposal types:', error);
    }
    // Feb-2026 Phase 3: load the equipment list for the "Assign
    // Equipment" panel. Shop-only exclusion (`field_use=false`) lives
    // on equipment_categories, not on the asset doc, so we fetch both
    // and filter client-side by joining through category name.
    try {
      const [assetsRes, catsRes] = await Promise.all([
        axios.get(`${API}/assets`),
        axios.get(`${API}/assets/equipment-categories`).catch(() => ({ data: [] })),
      ]);
      const rows = Array.isArray(assetsRes.data) ? assetsRes.data : [];
      const cats = Array.isArray(catsRes.data) ? catsRes.data : [];
      // Historical marker: every id we've EVER seen in the fleet at
      // load time — anything on the job's assigned list but not here
      // has been deleted from db.assets and gets flagged (round 5).
      setAllAssetIds(new Set(rows.map(a => a && a.id).filter(Boolean)));
      setAssetsLoaded(true);
      const shopOnlyCats = new Set(
        cats.filter(c => c && c.field_use === false).map(c => c.name)
      );
      setShopOnlyCatSet(shopOnlyCats);
      setAssetRows(rows);
    } catch (error) {
      console.error('Failed to fetch equipment list:', error);
    }
    try {
      const r = await axios.get(`${API}/cse-restrictions`);
      setCseRestrictions(r.data || { submitter_ids: [] });
    } catch (error) {
      console.error('Failed to fetch CSE restrictions:', error);
    }
  };

  const fetchJobReports = async (jobId) => {
    try {
      const [preconsRes, softDigsRes, locatesRes, cseRes] = await Promise.all([
        axios.get(`${API}/precons/job/${jobId}/list`).catch(() => ({ data: [] })),
        axios.get(`${API}/soft-digs/job/${jobId}/list`).catch(() => ({ data: [] })),
        axios.get(`${API}/locates/job/${jobId}/list`).catch(() => ({ data: [] })),
        // Confined Space lives in maintenance_forms (form_type filter),
        // same as FieldReports.js — no job-scoped list endpoint exists,
        // so we filter by job_id client-side (volume is small).
        axios.get(`${API}/maintenance?form_type=Confined Space Evaluation`).catch(() => ({ data: [] }))
      ]);
      // Maintenance uses draft/pending/approved/rejected; field reports
      // use draft/submitted_pending_review/completed — normalize so the
      // shared badge logic below renders correctly (same mapping as
      // FieldReports.js; rejected = editable again).
      const MAINT_TO_FR = { draft: 'draft', pending: 'submitted_pending_review', approved: 'completed', rejected: 'draft' };
      const cseForms = (cseRes.data || [])
        .filter(f => f.job_id === jobId)
        .map(f => ({ ...f, status: MAINT_TO_FR[(f.status || 'draft').toLowerCase()] || 'draft' }));
      setJobReports({
        precons: preconsRes.data || [],
        softDigs: softDigsRes.data || [],
        locates: locatesRes.data || [],
        cseForms
      });
    } catch (error) {
      console.error('Failed to fetch job reports:', error);
    }
  };

  const [timeRollup, setTimeRollup] = useState(null);
  const fetchTimeRollup = async (jobId) => {
    try {
      const res = await axios.get(`${API}/timesheets/by-job/${jobId}`);
      setTimeRollup(res.data);
    } catch (error) {
      console.error('Failed to fetch time rollup:', error);
      setTimeRollup(null);
    }
  };

  // Multi-day job functions
  const handleAddJobDay = async () => {
    if (!newDayData.date) {
      toast.error('Date is required');
      return;
    }
    
    // If editing an existing day
    if (editingDay) {
      try {
        const response = await axios.put(`${API}/jobs/${id}/days/${editingDay.id}`, {
          date: newDayData.date,
          time: newDayData.time,
          assigned_to: newDayData.assigned_to.length > 0 ? newDayData.assigned_to : formData.assigned_team_members,
          notes: newDayData.notes,
          service_type: newDayData.service_type || formData.service_type,
          assigned_line_items: newDayData.assigned_line_items,
          day_title: newDayData.day_title
        });
        
        setFormData(prev => ({
          ...prev,
          job_days: (prev.job_days || []).map(d => d.id === editingDay.id ? response.data : d)
        }));
        
        setShowAddDayDialog(false);
        setEditingDay(null);
        setNewDayData({ date: '', time: '', assigned_to: [], notes: '', service_type: '', assigned_line_items: [], day_title: '' });
        toast.success('Day updated');
      } catch (error) {
        toast.error('Failed to update day session');
      }
      return;
    }
    
    // If job is not yet created, add to pending days
    if (isNew) {
      const tempId = `temp-${Date.now()}`;
      const newDay = {
        id: tempId,
        day_number: pendingDays.length + 1,
        date: newDayData.date,
        time: newDayData.time || formData.time,
        assigned_to: newDayData.assigned_to.length > 0 ? newDayData.assigned_to : formData.assigned_team_members,
        assigned_names: team.filter(m => 
          (newDayData.assigned_to.length > 0 ? newDayData.assigned_to : formData.assigned_team_members).includes(m.id)
        ).map(m => m.full_name),
        notes: newDayData.notes,
        service_type: newDayData.service_type || formData.service_type,
        assigned_line_items: newDayData.assigned_line_items,
        day_title: newDayData.day_title
      };
      
      setPendingDays(prev => [...prev, newDay]);
      setShowAddDayDialog(false);
      setNewDayData({ date: '', time: '', assigned_to: [], notes: '' });
      toast.success(`Day ${newDay.day_number + 1} will be added when job is saved`);
      return;
    }
    
    // Add day to existing job
    try {
      const response = await axios.post(`${API}/jobs/${id}/days`, {
        date: newDayData.date,
        time: newDayData.time || formData.time,
        assigned_to: newDayData.assigned_to.length > 0 ? newDayData.assigned_to : formData.assigned_team_members,
        notes: newDayData.notes,
        service_type: newDayData.service_type || formData.service_type,
        assigned_line_items: newDayData.assigned_line_items,
        day_title: newDayData.day_title
      });
      
      setFormData(prev => ({
        ...prev,
        is_multi_day: true,
        job_days: [...(prev.job_days || []), response.data]
      }));
      
      setShowAddDayDialog(false);
      setNewDayData({ date: '', time: '', assigned_to: [], notes: '' });
      toast.success(`Day ${response.data.day_number + 1} added`);
    } catch (error) {
      toast.error('Failed to add day session');
    }
  };

  // Open dialog to edit an existing day
  const handleEditDay = (day) => {
    setEditingDay(day);
    setNewDayData({
      date: day.date,
      time: day.time || '',
      assigned_to: day.assigned_to || [],
      notes: day.notes || '',
      service_type: day.service_type || '',
      assigned_line_items: day.assigned_line_items || [],
      day_title: day.day_title || ''
    });
    setShowAddDayDialog(true);
  };

  // Delete a pending day (for new jobs before save)
  const handleDeletePendingDay = (tempId) => {
    setPendingDays(prev => prev.filter(d => d.id !== tempId).map((d, i) => ({ ...d, day_number: i + 1 })));
    toast.success('Day removed');
  };

  const handleUpdateJobDay = async (dayId, updates) => {
    try {
      const response = await axios.put(`${API}/jobs/${id}/days/${dayId}`, updates);
      
      setFormData(prev => ({
        ...prev,
        job_days: (prev.job_days || []).map(d => d.id === dayId ? response.data : d)
      }));
      
      setEditingDay(null);
      toast.success('Day session updated');
    } catch (error) {
      toast.error('Failed to update day session');
    }
  };

  const handleDeleteJobDay = async (dayId) => {
    try {
      const response = await axios.delete(`${API}/jobs/${id}/days/${dayId}`);
      
      setFormData(prev => ({
        ...prev,
        is_multi_day: response.data.remaining_days > 0,
        job_days: (prev.job_days || []).filter(d => d.id !== dayId).map((d, i) => ({ ...d, day_number: i + 1 }))
      }));
      
      toast.success('Day session removed');
    } catch (error) {
      toast.error('Failed to delete day session');
    }
  };

  useEffect(() => {
    fetchReferenceData();
    if (!isNew) {
      fetchJob();
      fetchJobReports(id);
      fetchTimeRollup(id);
    } else {
      // Check if this is a duplicate
      const duplicateData = sessionStorage.getItem('duplicateJobData');
      if (duplicateData) {
        try {
          const parsed = JSON.parse(duplicateData);
          setFormData(prev => ({ ...prev, ...parsed }));
          sessionStorage.removeItem('duplicateJobData');
        } catch (e) {
          console.error('Failed to parse duplicate job data:', e);
        }
      }
    }
  }, [id, isNew, fetchJob]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTeamMemberToggle = (memberId) => {
    setFormData(prev => {
      const current = prev.assigned_team_members || [];
      if (current.includes(memberId)) {
        // If we're removing this member and they're the current Team Lead,
        // clear team_lead_id too so we don't have a lead who isn't on the job.
        const nextLead = prev.team_lead_id === memberId ? null : prev.team_lead_id;
        return {
          ...prev,
          assigned_team_members: current.filter(id => id !== memberId),
          team_lead_id: nextLead,
        };
      } else {
        return { ...prev, assigned_team_members: [...current, memberId] };
      }
    });
  };

  // Feb-2026 Phase 3: equipment assigned to this job. Mirrors the
  // team-member toggle above — pure additive/removal of ids on the
  // form doc, no cost or workflow side-effects.
  const handleEquipmentToggle = (equipmentId) => {
    setFormData(prev => {
      const current = prev.assigned_equipment_ids || [];
      if (current.includes(equipmentId)) {
        return { ...prev, assigned_equipment_ids: current.filter(id => id !== equipmentId) };
      }
      return { ...prev, assigned_equipment_ids: [...current, equipmentId] };
    });
  };

  const handleSubmit = async (e, keepOnPage = false) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Build combined service_address from split fields
      const addressParts = [];
      if (formData.service_street) addressParts.push(formData.service_street);
      const cityStateZip = [
        formData.service_city,
        formData.service_state,
        formData.service_zip
      ].filter(Boolean).join(', ');
      if (cityStateZip) addressParts.push(cityStateZip);
      const combinedAddress = addressParts.join(', ') || formData.service_address;

      const payload = {
        ...formData,
        service_address: combinedAddress,
        duration: formData.duration ? parseFloat(formData.duration) : null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        labor_cost_per_hour: formData.labor_cost_per_hour ? parseFloat(formData.labor_cost_per_hour) : null,
      };

      // Prompt 40: a new job with no street can't be saved in a dispatch
      // status (backend would reject it) — force Pending here too, same
      // as Calendar's New Job, so this never dead-ends on save.
      const createdAsPendingForAddress = isNew && !formData.service_street;
      if (createdAsPendingForAddress) {
        payload.status = 'Pending';
      }

      if (isNew) {
        const response = await axios.post(`${API}/jobs`, payload);
        const newJobId = response.data.id;
        
        // If there are pending days, add them to the newly created job
        if (pendingDays.length > 0) {
          for (const day of pendingDays) {
            try {
              await axios.post(`${API}/jobs/${newJobId}/days`, {
                date: day.date,
                time: day.time,
                assigned_to: day.assigned_to,
                notes: day.notes
              });
            } catch (dayError) {
              console.error('Failed to add day:', dayError);
            }
          }
          setPendingDays([]);
          toast.success(createdAsPendingForAddress ? 'Created as Pending: site address needed' : `Job created with ${pendingDays.length + 1} days`);
        } else {
          toast.success(createdAsPendingForAddress ? 'Created as Pending: site address needed' : 'Job created - you can now add files');
        }
        
        if (keepOnPage && newJobId) {
          // Navigate to the created job to enable file uploads
          navigate(`/jobs/${newJobId}`);
        } else {
          navigate(backDestination);
        }
      } else {
        await axios.put(`${API}/jobs/${id}`, payload);
        toast.success('Job updated successfully');
        if (!keepOnPage) {
          navigate(backDestination);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (filesMode = 'trash') => {
    setDeleting(true);
    try {
      const r = await axios.delete(`${API}/jobs/${id}?files=${filesMode}`);
      const movedTo = r.data?.files_moved_to;
      if (movedTo) {
        toast.success(`Job deleted — files moved to ${movedTo.job_number || 'Day 1'} (${movedTo.date}).`);
      } else if (filesMode === 'trash') {
        toast.success('Job deleted — files moved to Trash (recoverable for 30 days).');
      } else if (filesMode === 'purge') {
        toast.success('Job and all files permanently deleted.');
      } else {
        toast.success('Job deleted');
      }
      navigate(backDestination);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Failed to delete job: ${detail}`);
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Total attachment count across all four buckets — drives the dialog
  // copy ("This job has N file(s)…") and whether we show the 3-option
  // picker vs the simple confirm.
  const fileCount = (formData.files?.length || 0)
    + (formData.photos?.length || 0)
    + (formData.tickets_811_files?.length || 0)
    + (formData.permit_files?.length || 0);
  const isSeriesDay = !!formData.daily_series_id;

  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  const handleDuplicateJob = () => setShowDuplicateDialog(true);

  const performDuplicate = (withFiles) => {
    // Create a copy of the current job data for a new job
    const cloneFile = (f) => ({
      ...f,
      id: (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    });
    const cloneList = (arr) => (withFiles && Array.isArray(arr)) ? arr.map(cloneFile) : [];

    const duplicatedData = {
      client_name: formData.client_name,
      contact_name: formData.contact_name,
      phone: formData.phone,
      email: formData.email,
      service_address: formData.service_address,
      service_street: formData.service_street,
      service_city: formData.service_city,
      service_state: formData.service_state,
      service_zip: formData.service_zip,
      job_title: `${formData.job_title} (Copy)`,
      service_type: formData.service_type,
      date: todayLocal(), // Reset to today
      time: formData.time,
      duration: formData.duration,
      priority: formData.priority,
      reminder: '',
      status: 'Pending', // Reset status
      notes: formData.notes,
      coordination_notes: formData.coordination_notes,
      field_notes: formData.field_notes,
      site_plan_notes: formData.site_plan_notes,
      is_recurring: formData.is_recurring,
      recurrence_pattern: formData.recurrence_pattern,
      assigned_team_members: formData.assigned_team_members,
      files: cloneList(formData.files),
      photos: cloneList(formData.photos),
      tickets_811_files: cloneList(formData.tickets_811_files),
      permit_files: cloneList(formData.permit_files),
    };

    // Store in sessionStorage and navigate to new job page
    sessionStorage.setItem('duplicateJobData', JSON.stringify(duplicatedData));
    setShowDuplicateDialog(false);
    navigate('/jobs/new?duplicate=true');
    toast.info(withFiles
      ? 'Job duplicated with files linked — review and save'
      : 'Job duplicated without files — review and save');
  };

  const handleCompanySelect = async (companyId) => {
    const company = contacts.find(c => c.id === companyId);
    if (!company) return;

    setSelectedCompanyId(companyId);

    // Set company name
    // Prompt 41: company selection must only fill client identity — never
    // the service/site address. A typed site address must survive
    // regardless of which company gets selected afterward.
    setFormData(prev => ({
      ...prev,
      client_name: company.company_name || company.client_name || '',
      client_id: companyId,
    }));

    // Fetch contacts/people for this company
    let companyContacts = company.people || company.contacts || [];

    if (companyContacts.length === 0) {
      try {
        const response = await axios.get(`${API}/people?company_id=${companyId}`);
        companyContacts = response.data || [];
      } catch (error) {
        console.error('Failed to fetch people for company:', error);
      }
    }

    setSelectedCompanyContacts(companyContacts);

    // If only one contact, auto-select
    if (companyContacts.length === 1) {
      setSelectedContactIdx('0');
      handleContactPersonSelect(companyContacts[0]);
    } else if (companyContacts.length > 1) {
      // Clear contact fields - user must select
      setSelectedContactIdx('');
      setFormData(prev => ({
        ...prev,
        contact_first_name: '',
        contact_last_name: '',
        contact_name: '',
        phone: '',
        email: ''
      }));
    } else {
      // No contacts - use company-level info
      setSelectedContactIdx('');
      setFormData(prev => ({
        ...prev,
        phone: company.phone1 || company.phone || prev.phone,
        email: company.email || prev.email
      }));
    }
  };

  const handleContactPersonSelect = (person) => {
    if (!person) return;
    const fullName = person.full_name || person.name || person.display_name || '';
    const nameParts = fullName.split(' ');
    const firstName = person.first_name || nameParts[0] || '';
    const lastName = person.last_name || nameParts.slice(1).join(' ') || '';

    setFormData(prev => ({
      ...prev,
      contact_first_name: firstName,
      contact_last_name: lastName,
      contact_name: fullName || `${firstName} ${lastName}`.trim(),
      phone: person.primary_phone || person.phone || person.mobile || prev.phone,
      email: person.primary_email || person.email || prev.email,
      contact_id: person.id || ''
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const canEdit = isAdmin() || hasPermission('edit_jobs');
  const canDelete = isAdmin() || hasPermission('delete_jobs');
  const canCreate = isAdmin() || hasPermission('create_jobs');

  // Every report on the Field Reports tile below belongs to THIS job, so
  // its own job_number/job_title/service_address is always the correct,
  // reliable identifying line — unlike each report's own fields
  // (project_name/site_location/soft_dig_number/job_service_address),
  // which are captured inconsistently depending on report type (Soft Dig
  // never captures a title; Confined Space captures neither a title nor
  // job_number on the report itself). One shared string, reused by all 4
  // report-type sections below (2026-09-01 consistency fix), matches the
  // "job code - title - address" reference format PreCon/Locate already
  // used for these listings.
  const jobIdentifyingSummary = [formData.job_number, formData.job_title, formData.service_address]
    .filter(Boolean)
    .join(' - ') || 'No title';

  if (isNew && !canCreate) {
    navigate(backDestination);
    return null;
  }

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        {/* Top row: Back, Title, Save, Close */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(backDestination)} className="h-9 p-2 shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="flex-1 text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            {isNew ? 'New Job' : 'Edit Job'}
          </h1>
          {(canEdit || isNew) && (
            <Button
              type="button"
              size="sm"
              onClick={(e) => handleSubmit(e, false)}
              className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shrink-0"
              disabled={saving}
              data-testid="save-job-top-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isNew ? 'Create Job' : 'Save Changes'}
                </>
              )}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            className="h-9 p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 shrink-0"
            onClick={() => navigate(backDestination)}
            title="Close without saving"
            data-testid="close-job-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Second row: Badges (when editing existing job) */}
        {!isNew && (
          <div className="flex flex-wrap items-center gap-2 px-1">
            {formData.job_number && (
              <Badge variant="outline" className="font-mono text-amber-600 border-amber-300 bg-amber-50">{formData.job_number}</Badge>
            )}
            {formData.date && (
              <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50">
                Week {getWeek(parseISO(formData.date))}
              </Badge>
            )}
            <Badge className={getStatusColor(formData.status)} style={getStatusStyle(formData.status) || undefined}>{formData.status}</Badge>
            <Badge className={getPriorityColor(formData.priority)}>{formData.priority}</Badge>
          </div>
        )}

        {/* Day Strip — visible on EVERY saved job so the office team can
            extend any job to multi-day without learning a different flow.
              • Series day-doc → show all sibling chips + "+ Add Day"
              • Single-day job → show "Day 1 of 1: <date>" + "+ Add Day"
                (clicking Add Day auto-converts to a series on the backend) */}
        {!isNew && formData.daily_series_id && dailySeries.length > 1 ? (
          <div className="flex flex-wrap items-center gap-2 px-1 py-2 bg-amber-50 rounded-lg border border-amber-200" data-testid="daily-series-strip">
            <span className="text-amber-800 font-medium text-sm mr-1">
              Day {formData.daily_series_position} of {formData.daily_series_total}:
            </span>
            {dailySeries.map((d, idx) => {
              const isActive = d.id === id;
              const position = idx + 1;
              let label;
              try {
                const dt = parseISO(d.date);
                label = `Day ${position}\u00A0\u00A0${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
              } catch {
                label = `Day ${position}`;
              }
              // Every chip is now a Popover. One tap → date editor for
              // THAT chip, plus an "Open this day →" link inside for
              // navigation. Avoids the previous 2-tap requirement (navigate,
              // then tap again for the editor) the user flagged.
              return (
                <DayChipPopover
                  key={d.id}
                  dayDoc={d}
                  position={position}
                  isActive={isActive}
                  label={label}
                  onNavigate={() => navigate(`/jobs/${d.id}`, { state: location.state })}
                  onAfterSave={fetchJob}
                />
              );
            })}
            {canEdit && (
              <Popover
                open={showAddSeriesDayDialog}
                onOpenChange={(open) => {
                  setShowAddSeriesDayDialog(open);
                  if (open) {
                    // Prefill with tomorrow + the current day-doc's crew —
                    // mirrors the previous Dialog defaults.
                    const t = new Date();
                    t.setDate(t.getDate() + 1);
                    setAddSeriesDate(t.toISOString().slice(0, 10));
                    setAddSeriesTeam(formData.assigned_team_members || []);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-dashed border-amber-400 text-amber-700 hover:bg-amber-100"
                    disabled={addingDay}
                    data-testid="daily-series-add-day-btn"
                    title="Extend this series by adding a new work day on a different date"
                  >
                    {addingDay ? 'Adding…' : '+ Add Day'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-80"
                  align="start"
                  data-testid="add-series-day-dialog"
                >
                  <div className="space-y-3">
                    <div>
                      <div className="font-semibold text-sm">Add Day to Series</div>
                      <p className="text-xs text-slate-600 mt-0.5">
                        Extends this multi-day job with a new work day.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="add-series-date" className="text-xs">Date</Label>
                      <Input
                        id="add-series-date"
                        type="date"
                        value={addSeriesDate}
                        onChange={(e) => setAddSeriesDate(e.target.value)}
                        autoFocus
                        data-testid="add-series-day-date-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Assign crew for this day</Label>
                      <div className="max-h-40 overflow-y-auto border rounded-md p-1.5 space-y-0.5 bg-slate-50">
                        {team.length === 0 ? (
                          <p className="text-xs text-slate-500 italic px-1.5 py-1">No team members available.</p>
                        ) : (
                          team.map((m) => {
                            const checked = addSeriesTeam.includes(m.id);
                            return (
                              <label
                                key={m.id}
                                className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1.5 py-0.5"
                                data-testid={`add-series-team-row-${m.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleAddSeriesTeamMember(m.id)}
                                  className="rounded"
                                />
                                <span className="text-sm text-slate-700">
                                  {m.full_name || m.email || m.id}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {addSeriesTeam.length === 0
                          ? 'No one assigned — day will be unmanned.'
                          : `${addSeriesTeam.length} assigned`}
                      </p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddSeriesDayDialog(false)}
                        disabled={addingDay}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddDayToSeries}
                        disabled={addingDay || !addSeriesDate}
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                        data-testid="add-series-day-confirm-btn"
                      >
                        {addingDay ? 'Adding…' : 'Add Day'}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        ) : (!isNew && !formData.daily_series_id && !formData.is_multi_day && formData.date && (
          <div className="flex flex-wrap items-center gap-2 px-1 py-2 bg-amber-50 rounded-lg border border-amber-200" data-testid="daily-series-strip">
            <span className="text-amber-800 font-medium text-sm mr-1">Day 1 of 1:</span>
            <Popover
              open={singleDayOpen}
              onOpenChange={(open) => {
                setSingleDayOpen(open);
                if (open) setSingleDayDate(formData.date || '');
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  data-testid="daily-series-jump-1"
                  title="Tap to change this day&rsquo;s date"
                >
              Day 1&nbsp;&nbsp;
              {(() => {
                try {
                  return parseISO(formData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                } catch {
                  return formData.date;
                }
              })()}
            </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72" align="start" data-testid="change-day-date-dialog">
                <div className="space-y-3">
                  <div>
                    <div className="font-semibold text-sm">Change this day&rsquo;s date</div>
                    <p className="text-xs text-slate-600 mt-0.5">Pick a new date for this job.</p>
                  </div>
                  <Input
                    type="date"
                    value={singleDayDate}
                    onChange={(e) => setSingleDayDate(e.target.value)}
                    autoFocus
                    data-testid="change-day-date-input"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSingleDayOpen(false)}
                      disabled={singleDaySaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveSingleDayDate}
                      disabled={singleDaySaving || !singleDayDate || singleDayDate === formData.date}
                      className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                      data-testid="change-day-date-save-btn"
                    >
                      {singleDaySaving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            {canEdit && (
              <Popover
                open={showAddSeriesDayDialog}
                onOpenChange={(open) => {
                  setShowAddSeriesDayDialog(open);
                  if (open) {
                    const t = new Date();
                    t.setDate(t.getDate() + 1);
                    setAddSeriesDate(t.toISOString().slice(0, 10));
                    setAddSeriesTeam(formData.assigned_team_members || []);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-dashed border-amber-400 text-amber-700 hover:bg-amber-100"
                    disabled={addingDay}
                    data-testid="daily-series-add-day-btn"
                    title="Extend this job to multiple days — adds a new day with its own crew and date"
                  >
                    {addingDay ? 'Adding…' : '+ Add Day'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="start" data-testid="add-series-day-dialog">
                  <div className="space-y-3">
                    <div>
                      <div className="font-semibold text-sm">Add Day to Job</div>
                      <p className="text-xs text-slate-600 mt-0.5">
                        Extends this single-day job to multiple days.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="add-series-date-single" className="text-xs">Date</Label>
                      <Input
                        id="add-series-date-single"
                        type="date"
                        value={addSeriesDate}
                        onChange={(e) => setAddSeriesDate(e.target.value)}
                        autoFocus
                        data-testid="add-series-day-date-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Assign crew for this day</Label>
                      <div className="max-h-40 overflow-y-auto border rounded-md p-1.5 space-y-0.5 bg-slate-50">
                        {team.length === 0 ? (
                          <p className="text-xs text-slate-500 italic px-1.5 py-1">No team members available.</p>
                        ) : (
                          team.map((m) => {
                            const checked = addSeriesTeam.includes(m.id);
                            return (
                              <label
                                key={m.id}
                                className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1.5 py-0.5"
                                data-testid={`add-series-team-row-${m.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleAddSeriesTeamMember(m.id)}
                                  className="rounded"
                                />
                                <span className="text-sm text-slate-700">
                                  {m.full_name || m.email || m.id}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {addSeriesTeam.length === 0
                          ? 'No one assigned — day will be unmanned.'
                          : `${addSeriesTeam.length} assigned`}
                      </p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddSeriesDayDialog(false)}
                        disabled={addingDay}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddDayToSeries}
                        disabled={addingDay || !addSeriesDate}
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                        data-testid="add-series-day-confirm-btn"
                      >
                        {addingDay ? 'Adding…' : 'Add Day'}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        ))}

        {/* Project Lineage Breadcrumb */}
        {canShowModule('job_information') && !isNew && (formData.opportunity_number || formData.proposal_number) && (
          <div className="flex items-center gap-2 px-1 py-2 bg-slate-50 rounded-lg text-sm" data-testid="job-info-module">
            <span className="text-slate-500 font-medium">Project Lineage:</span>
            {formData.opportunity_number && (
              <>
                <Link 
                  to={`/opportunities?id=${formData.opportunity_id}`}
                  className="text-amber-600 hover:text-amber-700 hover:underline font-mono"
                >
                  {formData.opportunity_number}
                </Link>
                <span className="text-slate-400">→</span>
              </>
            )}
            {formData.proposal_number && (
              <>
                <Link 
                  to={`/proposals?id=${formData.proposal_id}`}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-mono"
                >
                  {formData.proposal_number}
                </Link>
                <span className="text-slate-400">→</span>
              </>
            )}
            <span className="text-green-600 font-mono font-medium">{formData.job_number}</span>
          </div>
        )}
        
        {/* Action toolbar (when editing existing job) — consolidated row */}
        {!isNew && (
          <div className="flex flex-wrap items-center gap-2 px-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => navigate(`/timesheet?job_id=${id}&job_number=${encodeURIComponent(formData.job_number || '')}&job_title=${encodeURIComponent(formData.job_title)}&date=${formData.date || ''}`)}
              data-testid="log-time-btn"
            >
              <Clock className="w-4 h-4 mr-1.5" />
              Log Time
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => navigate(`/expense-reports?job_id=${id}&job_number=${encodeURIComponent(formData.job_number || '')}`)}
              data-testid="add-expense-btn"
            >
              <DollarSign className="w-4 h-4 mr-1.5" />
              Add Expense
            </Button>
            {canDelete && (
              <>
                <span className="h-5 w-px bg-slate-200 mx-1" aria-hidden="true" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
                  onClick={handleDuplicateJob}
                  data-testid="duplicate-job-btn"
                >
                  <Copy className="w-4 h-4 mr-1.5" />
                  Duplicate
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-job-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  Delete
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            {canShowModule('client_information') && (
            <Card className="border-slate-200" data-testid="client-info-module">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed']">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Company and Contact Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {contacts.length > 0 && (
                    <div className="space-y-2">
                      <Label>Select Company</Label>
                      <Select onValueChange={handleCompanySelect} value={selectedCompanyId} disabled={!canEdit && !isNew}>
                        <SelectTrigger className="w-full" data-testid="company-select">
                          <SelectValue placeholder="Select Company" />
                        </SelectTrigger>
                        <SelectContent>
                          {contacts.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {selectedCompanyContacts.length > 0 && (
                    <div className="space-y-2">
                      <Label>Select Contact Person</Label>
                      <Select
                        value={selectedContactIdx}
                        disabled={!canEdit && !isNew}
                        onValueChange={(idx) => {
                          setSelectedContactIdx(idx);
                          handleContactPersonSelect(selectedCompanyContacts[parseInt(idx)]);
                        }}
                      >
                        <SelectTrigger className="w-full" data-testid="contact-person-select">
                          <SelectValue placeholder="Select Contact Person" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedCompanyContacts.map((p, idx) => (
                            <SelectItem key={p.id || idx} value={idx.toString()}>
                              {p.full_name || p.name || p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Contact ${idx + 1}`}
                              {(p.job_title || p.title) && ` - ${p.job_title || p.title}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {selectedCompanyId && selectedCompanyContacts.length === 0 && (
                    <div className="flex items-center text-sm text-slate-500 italic pt-7">
                      No contacts for this company
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_name">Client Name *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => handleChange('client_name', e.target.value)}
                    required
                    disabled={!canEdit && !isNew}
                    className="bg-slate-50"
                    placeholder="Enter client or company name"
                    data-testid="client-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_first_name">First Name *</Label>
                    <Input
                      id="contact_first_name"
                      value={formData.contact_first_name || ''}
                      onChange={(e) => {
                        handleChange('contact_first_name', e.target.value);
                        handleChange('contact_name', `${e.target.value} ${formData.contact_last_name || ''}`.trim());
                      }}
                      disabled={!canEdit && !isNew}
                      className="h-10 bg-slate-50"
                      placeholder="First name"
                      data-testid="contact-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_last_name">Last Name *</Label>
                    <Input
                      id="contact_last_name"
                      value={formData.contact_last_name || ''}
                      onChange={(e) => {
                        handleChange('contact_last_name', e.target.value);
                        handleChange('contact_name', `${formData.contact_first_name || ''} ${e.target.value}`.trim());
                      }}
                      disabled={!canEdit && !isNew}
                      className="h-10 bg-slate-50"
                      placeholder="Last name"
                      data-testid="contact-last-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      disabled={!canEdit && !isNew}
                      className="h-10 bg-slate-50"
                      data-testid="phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      disabled={!canEdit && !isNew}
                      className="h-10 bg-slate-50"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                {/* Quick Actions */}
                {!isNew && (formData.phone || formData.email) && (
                  <div className="flex gap-2 pt-2">
                    {formData.phone && (
                      <a href={`tel:${formData.phone}`}>
                        <Button type="button" variant="outline" size="sm">
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </Button>
                      </a>
                    )}
                    {formData.email && (
                      <a href={`mailto:${formData.email}`}>
                        <Button type="button" variant="outline" size="sm">
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </Button>
                      </a>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Job Details */}
            {canShowModule('job_details') && (
            <Card className="border-slate-200" data-testid="job-details-module">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed']">Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="job_title">Job Title *</Label>
                  <Input
                    id="job_title"
                    value={formData.job_title}
                    onChange={(e) => handleChange('job_title', e.target.value)}
                    required
                    disabled={!canEdit && !isNew}
                    className="bg-slate-50"
                    data-testid="job-title-input"
                  />
                </div>

                {/* Service Address Section - Where the work will be performed */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-semibold text-green-800 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        📍 Service Address (Work Location)
                        {!formData.service_street && (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200 font-normal" data-testid="job-detail-address-needed-badge">
                            Site address needed
                          </Badge>
                        )}
                      </Label>
                      <p className="text-xs text-green-600 mt-1">Where the field work will be performed</p>
                    </div>
                    {(formData.service_street || formData.service_address) && (
                      <MapButtons 
                        address={[
                          formData.service_street,
                          formData.service_city,
                          formData.service_state,
                          formData.service_zip
                        ].filter(Boolean).join(', ') || formData.service_address}
                        size="sm"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service_street" className="text-green-700">Street Address *</Label>
                    <Input
                      id="service_street"
                      value={formData.service_street || ''}
                      onChange={(e) => handleChange('service_street', e.target.value)}
                      placeholder="123 Main Street or site description"
                      disabled={!canEdit && !isNew}
                      className="bg-white"
                      data-testid="service-street-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="service_city">City</Label>
                      <Input
                        id="service_city"
                        value={formData.service_city || ''}
                        onChange={(e) => handleChange('service_city', e.target.value)}
                        placeholder="City"
                        disabled={!canEdit && !isNew}
                        className="bg-white"
                        data-testid="service-city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service_state">State</Label>
                      <Input
                        id="service_state"
                        value={formData.service_state || ''}
                        onChange={(e) => handleChange('service_state', e.target.value)}
                        placeholder="NC"
                        disabled={!canEdit && !isNew}
                        className="bg-white"
                        data-testid="service-state-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service_zip">ZIP Code</Label>
                      <Input
                        id="service_zip"
                        value={formData.service_zip || ''}
                        onChange={(e) => handleChange('service_zip', e.target.value)}
                        placeholder="28401"
                        disabled={!canEdit && !isNew}
                        className="bg-white"
                        data-testid="service-zip-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Address history — super users only, collapsed by default (Prompt 33) */}
                {isSuperUser() && !isNew && (
                  <Accordion
                    type="single"
                    collapsible
                    onValueChange={(v) => { if (v === 'address-history') fetchAddressHistory(); }}
                    data-testid="address-history-accordion"
                  >
                    <AccordionItem value="address-history" className="border border-slate-200 rounded-lg px-4 bg-slate-50">
                      <AccordionTrigger data-testid="address-history-trigger" className="text-sm font-semibold text-slate-700 hover:no-underline">
                        Address history
                      </AccordionTrigger>
                      <AccordionContent>
                        {addressHistoryLoading ? (
                          <p className="text-sm text-slate-500" data-testid="address-history-loading">Loading…</p>
                        ) : addressHistory.length === 0 ? (
                          <p className="text-sm text-slate-500" data-testid="address-history-empty">No address changes recorded for this job.</p>
                        ) : (
                          <ul className="space-y-3" data-testid="address-history-list">
                            {addressHistory.map((entry) => (
                              <li key={entry.id} className="text-sm border-l-2 border-slate-300 pl-3" data-testid="address-history-entry">
                                <div className="text-xs text-slate-500">
                                  {entry.changed_by || 'Unknown user'} • {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : ''}
                                </div>
                                {entry.event === 'created' ? (
                                  <div className="mt-1" data-testid="address-history-created-entry">
                                    <span className="font-medium text-slate-700">
                                      Created ({ADDRESS_SOURCE_LABELS[entry.source] || entry.source || 'unknown source'}):
                                    </span>{' '}
                                    <span className="text-slate-700">
                                      {[entry.address?.street, entry.address?.city, entry.address?.state, entry.address?.zip].filter(Boolean).join(', ') || '(blank)'}
                                    </span>
                                  </div>
                                ) : (
                                  Object.entries(entry.changes || {}).map(([field, diff]) => (
                                    <div key={field} className="mt-1">
                                      <span className="font-medium text-slate-700">{ADDRESS_FIELD_LABELS[field] || field}:</span>{' '}
                                      <span className="text-red-600 line-through">{diff.old || '(blank)'}</span>{' '}
                                      <span className="text-slate-400">→</span>{' '}
                                      <span className="text-green-700">{diff.new || '(blank)'}</span>
                                    </div>
                                  ))
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="service_type">Proposal Service Type *</Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(v) => {
                        handleChange('service_type', v);
                        if (v !== 'Other') {
                          handleChange('custom_service_type', '');
                        }
                      }}
                      disabled={!canEdit && !isNew}
                    >
                      <SelectTrigger className="bg-slate-50" data-testid="service-type-select">
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        {proposalTypes.filter(t => t.name !== 'Other').map(t => (
                          <SelectItem key={t.id || t.name} value={t.name}>
                            {t.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="Other" className="border-t border-slate-200 mt-1 pt-1">
                          <span className="text-slate-600 italic">Other (specify below)</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.service_type === 'Other' && (
                      <Input
                        value={formData.custom_service_type || ''}
                        onChange={(e) => handleChange('custom_service_type', e.target.value)}
                        placeholder="Describe the service type..."
                        className="bg-amber-50 border-amber-300 mt-2"
                        disabled={!canEdit && !isNew}
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status *</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => handleChange('status', v)}
                      disabled={!canEdit && !isNew}
                    >
                      <SelectTrigger className="bg-slate-50" data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Feb-2026: a job's current status may be an inactive
                            status (e.g. Pending). Always show it so the
                            dropdown never renders blank, without adding it
                            to the active list other jobs pick from. */}
                        {formData.status && !jobStatuses.some(s => s.name === formData.status) && (
                          <SelectItem key="current-status" value={formData.status}>{formData.status}</SelectItem>
                        )}
                        {jobStatuses.map(status => (
                          <SelectItem key={status.id} value={status.name}>{status.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Service Line Items from Proposal - Full width section */}
                {canShowModule('proposals') && formData.line_items && formData.line_items.length > 0 && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="proposals-module">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">
                      Service Line Items (from Proposal)
                    </p>
                    {/* Table header */}
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-blue-700 uppercase tracking-wide pb-2 border-b border-blue-200 mb-2">
                      <div className="col-span-5">Service</div>
                      <div className="col-span-5">Description</div>
                      <div className="col-span-2 text-right">Qty</div>
                    </div>
                    {/* Table rows */}
                    <ul className="space-y-2">
                      {formData.line_items.map((item, idx) => {
                        // Data mapping - handle both old and new formats:
                        // New format: service = service name, custom_description = details
                        // Old format: description = service name OR details
                        const serviceName = item.service || item.name || item.description || '—';
                        const descriptionText = item.custom_description || (item.service ? item.description : '') || '—';
                        
                        return (
                          <li key={idx} className="grid grid-cols-12 gap-2 items-center text-sm text-blue-900 p-2 bg-white rounded border border-blue-100">
                            <div className="col-span-5 flex items-center gap-2">
                              <span className="w-5 h-5 flex items-center justify-center bg-blue-500 rounded-full text-xs font-bold text-white flex-shrink-0">
                                {idx + 1}
                              </span>
                              <span className="font-medium truncate">{serviceName}</span>
                            </div>
                            <div className="col-span-5 text-slate-600 truncate">
                              {descriptionText}
                            </div>
                            <div className="col-span-2 text-right font-medium">
                              {item.quantity || 1}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Start Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleChange('date', e.target.value)}
                      required
                      disabled={!canEdit && !isNew}
                      className="bg-slate-50"
                      data-testid="date-input"
                    />
                    {formData.date && (
                      <p className="text-xs text-blue-600 font-medium">
                        Week {getWeek(parseISO(formData.date))}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date</Label>
                    {formData.daily_series_id ? (
                      <>
                        <Input
                          id="end_date"
                          type="date"
                          value=""
                          disabled
                          className="bg-slate-100 text-slate-400"
                          data-testid="end-date-input"
                          placeholder="—"
                        />
                        <p className="text-xs text-amber-700">
                          This is a series day-doc (single day only). To add another day, use <strong>+ Add Day</strong> on the day-chip strip above.
                        </p>
                      </>
                    ) : (
                      <>
                        <Input
                          id="end_date"
                          type="date"
                          value={formData.end_date || formData.date || ''}
                          onChange={(e) => handleChange('end_date', e.target.value)}
                          disabled={!canEdit && !isNew}
                          className="bg-slate-50"
                          data-testid="end-date-input"
                        />
                        <p className="text-xs text-slate-500">Job spans from Start to End Date on calendar</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) => handleChange('priority', v)}
                      disabled={!canEdit && !isNew}
                    >
                      <SelectTrigger className="bg-slate-50" data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Normal">Normal</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reminder">Reminder</Label>
                    <Input
                      id="reminder"
                      type="datetime-local"
                      value={formData.reminder || ''}
                      onChange={(e) => handleChange('reminder', e.target.value)}
                      disabled={!canEdit && !isNew}
                      className="bg-slate-50"
                    />
                  </div>
                </div>

                {/* Estimated hours & labor cost — friction #19. Used for the
                    Time-Spent tile variance ("Est vs Actual"). */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="estimated_hours">Estimated Hours</Label>
                    <Input
                      id="estimated_hours"
                      type="number"
                      step="0.25"
                      min="0"
                      value={formData.estimated_hours || ''}
                      onChange={(e) => handleChange('estimated_hours', e.target.value)}
                      placeholder="e.g. 8"
                      disabled={!canEdit && !isNew}
                      className="bg-slate-50"
                      data-testid="estimated-hours-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="labor_cost_per_hour">Labor Cost / hr (optional)</Label>
                    <Input
                      id="labor_cost_per_hour"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.labor_cost_per_hour || ''}
                      onChange={(e) => handleChange('labor_cost_per_hour', e.target.value)}
                      placeholder="$ used for Hours-by-Job report"
                      disabled={!canEdit && !isNew}
                      className="bg-slate-50"
                      data-testid="labor-cost-input"
                    />
                  </div>
                </div>

                {/* Coordination Notes - visible to everyone */}
                <div className="space-y-2" data-testid="coordination-notes-section">
                  <Label htmlFor="coordination_notes">
                    Coordination Notes
                  </Label>
                  <Textarea
                    id="coordination_notes"
                    value={formData.coordination_notes || ''}
                    onChange={(e) => handleChange('coordination_notes', e.target.value)}
                    rows={4}
                    disabled={!canEdit && !isNew}
                    className="bg-slate-50 min-h-[100px] resize-y"
                    data-testid="coordination-notes-input"
                    placeholder="Scheduling, billing, or office coordination notes..."
                  />
                </div>

                {/* Field Notes - visible to everyone */}
                <div className="space-y-2" data-testid="field-notes-section">
                  <Label htmlFor="field_notes">
                    Field Notes
                  </Label>
                  <Textarea
                    id="field_notes"
                    value={formData.field_notes || ''}
                    onChange={(e) => handleChange('field_notes', e.target.value)}
                    rows={5}
                    disabled={!canEdit && !isNew}
                    className="bg-slate-50 min-h-[120px] resize-y"
                    data-testid="field-notes-input"
                    placeholder="Job site instructions, special requirements, or anything the crew needs on-site..."
                  />
                </div>

                {/* Middle Save Button */}
                {(canEdit || isNew) && (
                  <div className="pt-4 border-t border-slate-200 mt-6">
                    <Button 
                      type="button"
                      onClick={(e) => handleSubmit(e, false)}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                      disabled={saving}
                      data-testid="save-job-middle-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {isNew ? 'Create Job' : 'Save Changes'}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Team Assignment */}
            {canShowModule('assign_team_members') && (
            <Card className="border-slate-200" data-testid="assign-team-members-module">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed']">Assign Team Members</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {team.filter(m => m.active).map(member => {
                    const isAssigned = formData.assigned_team_members?.includes(member.id);
                    const isLead = formData.team_lead_id === member.id;
                    return (
                    <div
                      key={member.id}
                      className="flex items-center space-x-3 p-2 rounded-md hover:bg-slate-50"
                    >
                      <Checkbox
                        id={`team-${member.id}`}
                        checked={isAssigned}
                        onCheckedChange={() => handleTeamMemberToggle(member.id)}
                        disabled={!canEdit && !isNew}
                      />
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-sm"
                        style={{ backgroundColor: member.calendar_color || '#f59e0b', color: getContrastTextColor(member.calendar_color || '#f59e0b') }}
                      >
                        {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <Label htmlFor={`team-${member.id}`} className="cursor-pointer flex-1">
                        {member.full_name}
                        {isLead && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                            <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                            Lead
                          </span>
                        )}
                      </Label>
                      {isAssigned && (canEdit || isNew) && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              team_lead_id: prev.team_lead_id === member.id ? null : member.id,
                            }));
                          }}
                          title={isLead ? 'Remove as Team Lead' : 'Set as Team Lead'}
                          data-testid={`team-lead-toggle-${member.id}`}
                          className={`p-1 rounded transition-colors ${
                            isLead
                              ? 'text-amber-500 hover:bg-amber-50'
                              : 'text-slate-300 hover:text-amber-400 hover:bg-amber-50'
                          }`}
                          aria-label={isLead ? `Remove ${member.full_name} as Team Lead` : `Set ${member.full_name} as Team Lead`}
                        >
                          <Star className={`w-5 h-5 ${isLead ? 'fill-amber-500 text-amber-500' : ''}`} />
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Feb-2026 Phase 3: Assign Equipment
                Mirrors the Assign Team Members panel above. Pure
                usage/tracking record — costing is handled elsewhere.
                Grouped by parent category so a long asset list stays
                scannable. Filtered client-side to `field_use !== false`. */}
            {(canShowModule ? canShowModule('assign_team_members') : true) && (
            <Card className="border-slate-200" data-testid="assign-equipment-module">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed']">Assign Equipment</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Round 5: lock the list once the job is Completed
                    (same auto-archive trigger the backend uses). If
                    the job is reopened to a non-Completed status,
                    formData.status flips and this flag flips too — no
                    manual unlock required. Renders an inline banner
                    so the user knows why nothing is clickable. */}
                {(() => {
                  const equipmentLocked = formData.status === 'Completed';
                  // Historical marker for equipment deleted from the
                  // fleet after being assigned — only compute once we
                  // KNOW /api/assets loaded, otherwise a network hiccup
                  // would show a scary "everything is missing" banner.
                  const orphanIds = assetsLoaded
                    ? (formData.assigned_equipment_ids || []).filter(
                        id => !allAssetIds.has(id),
                      )
                    : [];
                  const orphanCount = orphanIds.length;
                  // Feb 2026 — resolve orphan names from the snapshot
                  // stored on the job at save time. Falls back to the
                  // raw id when the snapshot predates this feature.
                  const nameSnapshot = formData.assigned_equipment_snapshot || {};
                  const orphanNames = orphanIds.map(
                    id => nameSnapshot[id] || `Unknown (${String(id).slice(0, 8)})`,
                  );
                  return (
                    <>
                      {equipmentLocked && (
                        <div
                          className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                          data-testid="assign-equipment-locked-banner"
                        >
                          🔒 This job is Completed — the equipment list is locked. Reopen the job (change status back to Scheduled / In Progress) to edit.
                        </div>
                      )}
                      {!equipmentLocked && !canAssignEquipment && (
                        <div
                          className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                          data-testid="assign-equipment-no-permission-banner"
                        >
                          🔒 You don't have permission to assign equipment to jobs. Ask an admin to enable "Assign Equipment to Jobs" for your account.
                        </div>
                      )}
                      {orphanCount > 0 && (
                        <div
                          className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
                          data-testid="assign-equipment-orphan-notice"
                        >
                          <span className="font-semibold text-slate-700">
                            {orphanCount} {orphanCount === 1 ? 'piece' : 'pieces'} of equipment
                          </span>
                          {' '}assigned to this job {orphanCount === 1 ? 'has' : 'have'} been removed from the fleet. The historical record is preserved but the specific {orphanCount === 1 ? 'item is' : 'items are'} no longer available.
                          <ul className="list-disc pl-5 mt-2 space-y-0.5" data-testid="orphan-name-list">
                            {orphanNames.map((n, i) => (
                              <li key={orphanIds[i]}>{n}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {equipmentList.length === 0 && orphanCount === 0 ? (
                        <p className="text-sm text-slate-500 italic">No equipment available.</p>
                      ) : equipmentList.length === 0 ? null : (
                        <div className="space-y-4">
                          {(() => {
                            const groups = {};
                            equipmentList.forEach(a => {
                              const cat = a.category || 'Uncategorized';
                              const sub = a.subcategory_name || '(Unassigned)';
                              groups[cat] = groups[cat] || {};
                              (groups[cat][sub] = groups[cat][sub] || []).push(a);
                            });
                            const categories = Object.keys(groups).sort();
                            const sortSubs = (subKeys) => [...subKeys].sort((x, y) => {
                              if (x === '(Unassigned)') return 1;
                              if (y === '(Unassigned)') return -1;
                              return x.localeCompare(y);
                            });
                            const catTotal = (cat) => Object.values(groups[cat]).reduce((n, arr) => n + arr.length, 0);
                            const catAssigned = (cat) => Object.values(groups[cat]).reduce(
                              (n, arr) => n + arr.filter(a => formData.assigned_equipment_ids?.includes(a.id)).length,
                              0,
                            );
                            const renderRow = (equip) => {
                              const isAssigned = formData.assigned_equipment_ids?.includes(equip.id);
                              return (
                                <div
                                  key={equip.id}
                                  className="flex items-center space-x-3 p-2 rounded-md hover:bg-slate-50"
                                  data-testid={`assign-equipment-row-${equip.asset_number || equip.id}`}
                                >
                                  <Checkbox
                                    id={`equip-${equip.id}`}
                                    checked={!!isAssigned}
                                    onCheckedChange={() => handleEquipmentToggle(equip.id)}
                                    disabled={equipmentLocked || (!canEdit && !isNew) || !canAssignEquipment}
                                    data-testid={`assign-equipment-checkbox-${equip.asset_number || equip.id}`}
                                  />
                                  <Label htmlFor={`equip-${equip.id}`} className="cursor-pointer flex-1">
                                    <span className="font-medium text-slate-800">{equip.name}</span>
                                    {equip.asset_number && (
                                      <span className="ml-2 text-xs text-slate-500">({equip.asset_number})</span>
                                    )}
                                    {BLOCKED_EQUIPMENT_STATUSES.has(equip.status) && (
                                      <span className="ml-2 text-[10px] font-semibold uppercase text-red-600" data-testid={`assign-equipment-status-badge-${equip.asset_number || equip.id}`}>
                                        {equip.status}
                                      </span>
                                    )}
                                  </Label>
                                </div>
                              );
                            };

                            return (
                              <Accordion
                                type="multiple"
                                value={assignEquipmentOpen}
                                onValueChange={handleAssignEquipmentOpenChange}
                                className="space-y-1"
                                data-testid="assign-equipment-accordion"
                              >
                                {categories.map(cat => {
                                  const subNames = sortSubs(Object.keys(groups[cat]));
                                  const soleSub = subNames.length === 1;
                                  const catValue = `assign-cat-${cat}`;
                                  const openedAt = assignEquipmentOpen.includes(catValue) ? assignEquipmentOpenedAt[catValue] : null;
                                  return (
                                    <AccordionItem
                                      key={cat}
                                      value={catValue}
                                      className="border border-slate-200 rounded-lg overflow-hidden"
                                      data-testid={`assign-equipment-category-group-${cat}`}
                                    >
                                      <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-slate-50">
                                        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                          {cat}
                                          <span className="text-xs text-slate-400 font-normal">
                                            ({catAssigned(cat)}/{catTotal(cat)})
                                          </span>
                                          {openedAt && (
                                            <span
                                              className="text-[11px] font-normal text-amber-700 italic"
                                              data-testid={`assign-equipment-opened-note-${cat}`}
                                            >
                                              Opened by you {(Date.now() - new Date(openedAt).getTime()) < 60000 ? 'just now' : formatDistanceToNow(new Date(openedAt), { addSuffix: true })}
                                            </span>
                                          )}
                                        </span>
                                      </AccordionTrigger>
                                      <AccordionContent className="px-3 pb-3 pt-1">
                                        {soleSub ? (
                                          <div className="space-y-1">
                                            {groups[cat][subNames[0]]
                                              .slice()
                                              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                              .map(renderRow)}
                                          </div>
                                        ) : (
                                          <div className="space-y-3">
                                            {subNames.map(subName => (
                                              <div key={subName}>
                                                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5 pl-1">
                                                  {subName}
                                                  <span className="text-slate-400 font-normal ml-1.5">({groups[cat][subName].length})</span>
                                                </p>
                                                <div className="space-y-1">
                                                  {groups[cat][subName]
                                                    .slice()
                                                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                    .map(renderRow)}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </AccordionContent>
                                    </AccordionItem>
                                  );
                                })}
                              </Accordion>
                            );
                          })()}
                        </div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
            )}

            {/* Multi-Day Job Section - Hidden, replaced by Start/End Date + Duplicate */}
            {false && (
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-['Barlow_Condensed']">Multi-Day Job</CardTitle>
                {(canEdit || isNew) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingDay(null);
                      setNewDayData({ date: '', time: '', assigned_to: [], notes: '', service_type: '', assigned_line_items: [], day_title: '' });
                      setShowAddDayDialog(true);
                    }}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="add-day-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Day
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {/* For new jobs - show pending days */}
                {isNew && pendingDays.length > 0 && (
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-blue-800">Day 1</span>
                          <span className="text-sm text-slate-600 ml-2">{formData.date || 'Set job date'}</span>
                        </div>
                        <Badge variant="outline" className="text-blue-600 border-blue-300">Original</Badge>
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        {team.filter(m => formData.assigned_team_members?.includes(m.id)).map(m => m.full_name).join(', ') || 'Assign team above'}
                      </div>
                    </div>
                    {pendingDays.map((day) => (
                      <div key={day.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <span className="font-semibold text-blue-800">Day {day.day_number + 1}</span>
                            <span className="text-sm text-slate-600 ml-2">{day.date}</span>
                            {day.time && <span className="text-sm text-slate-500 ml-2">@ {day.time}</span>}
                            <Badge variant="outline" className="ml-2 text-blue-600 border-blue-300 text-xs">Pending Save</Badge>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePendingDay(day.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="mt-1 text-sm text-slate-600">
                          {day.service_type && day.service_type !== formData.service_type && (
                            <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded mr-2">{day.service_type}</span>
                          )}
                          {day.assigned_names?.join(', ') || 'Same as Day 1'}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                      Additional days will be created when you save the job.
                    </p>
                  </div>
                )}

                {/* For new jobs with no pending days */}
                {isNew && pendingDays.length === 0 && (
                  <div className="text-center py-4 text-slate-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Single day job</p>
                    <p className="text-xs text-slate-400 mt-1">Click "Add Day" to make this a multi-day job</p>
                  </div>
                )}

                {/* For existing jobs with multi-day */}
                {!isNew && formData.is_multi_day && formData.job_days?.length > 0 ? (
                  <div className="space-y-3">
                    {/* Day 1 is the original job date */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-blue-800">Day 1</span>
                          <span className="text-sm text-slate-600 ml-2">{formData.date}</span>
                        </div>
                        <Badge variant="outline" className="text-blue-600 border-blue-300">Original</Badge>
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        {team.filter(m => formData.assigned_team_members?.includes(m.id)).map(m => m.full_name).join(', ') || 'No workers assigned'}
                      </div>
                    </div>
                    
                    {/* Additional days */}
                    {formData.job_days.map((day) => (
                      <div key={day.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 cursor-pointer" onClick={() => canEdit && handleEditDay(day)}>
                            <span className="font-semibold text-slate-800">Day {day.day_number + 1}</span>
                            <span className="text-sm text-slate-600 ml-2">{day.date}</span>
                            {day.time && <span className="text-sm text-slate-500 ml-2">@ {day.time}</span>}
                            {day.day_title && (
                              <div className="text-sm font-medium text-slate-700 mt-0.5">{day.day_title}</div>
                            )}
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditDay(day)}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-6 w-6 p-0"
                                title="Edit day"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteJobDay(day.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                                title="Delete day"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-slate-600">
                          {day.service_type && day.service_type !== formData.service_type && (
                            <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded mr-2">{day.service_type}</span>
                          )}
                          {day.assigned_names?.join(', ') || 'Same as Day 1'}
                        </div>
                        {day.assigned_line_items?.length > 0 && (formData.service_line_items || formData.line_items) && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {day.assigned_line_items.map(idx => {
                              const item = (formData.service_line_items || formData.line_items || [])[idx];
                              if (!item) return null;
                              return (
                                <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                  {item.service_name || item.service || item.name}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {day.notes && (
                          <div className="mt-2 text-xs text-slate-500 italic">{day.notes}</div>
                        )}
                        {canEdit && (
                          <div className="mt-1 text-xs text-blue-500">Click to edit</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : !isNew && (
                  <div className="text-center py-4 text-slate-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Single day job</p>
                    <p className="text-xs text-slate-400 mt-1">Click "Add Day" to extend this job</p>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Files & Photos - Only show for existing jobs */}
            {!isNew ? (
              <>
                {/* Banner: explain the new "files shared across days" behavior
                    so office staff isn't surprised when an upload on Day 1
                    also appears on Days 2+ (and vice versa). Added 2026-02-09
                    alongside the series-file-inheritance + propagation work. */}
                {formData.daily_series_id && (
                  <div
                    className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900"
                    data-testid="files-shared-banner"
                  >
                    <FolderOpen className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                    <div>
                      <span className="font-semibold">Files are shared across all days in this series.</span>{' '}
                      Anything you upload here will appear on every other day of <em>{formData.job_title?.replace(/\s+—\s+Day\s+\d+\s*$/, '') || 'this job'}</em>, and deleting it from one day leaves the others untouched.
                    </div>
                  </div>
                )}
                {/* Job Files - Using reusable FileUpload component */}
                <FileUpload
                  files={(formData.files || []).map(f => ({
                    ...f,
                    filename: f.filename,
                    uploaded_at: f.uploaded_at,
                  }))}
                  uploadEndpoint={`/api/jobs/${id}/files`}
                  deleteEndpoint={`/api/jobs/${id}/files`}
                  onUploadSuccess={refreshAttachments}
                  onDeleteSuccess={refreshAttachments}
                  title="Job Files"
                  canEdit={canEdit}
                  emptyMessage="No files attached to this job"
                />

                {/* Job Photos - Using reusable FileUpload component */}
                {canShowModule('job_photos') && (
                <div data-testid="job-photos-module">
                <FileUpload
                  files={(formData.photos || []).map(p => ({
                    ...p,
                    filename: p.filename,
                    uploaded_at: p.uploaded_at,
                  }))}
                  uploadEndpoint={`/api/jobs/${id}/photos`}
                  deleteEndpoint={`/api/jobs/${id}/photos`}
                  onUploadSuccess={refreshAttachments}
                  onDeleteSuccess={refreshAttachments}
                  title="Job Photos"
                  canEdit={canEdit}
                  accept="image/*"
                  emptyMessage="No photos attached to this job"
                />
                </div>
                )}

                {/* Job 811 Tickets - Using reusable FileUpload component */}
                {canShowModule('job_811_tickets') && (
                <div data-testid="job-811-tickets-module">
                <FileUpload
                  files={(formData.tickets_811_files || []).map(f => ({
                    ...f,
                    filename: f.filename,
                    uploaded_at: f.uploaded_at,
                  }))}
                  uploadEndpoint={`/api/jobs/${id}/811-tickets`}
                  deleteEndpoint={`/api/jobs/${id}/811-tickets`}
                  onUploadSuccess={refreshAttachments}
                  onDeleteSuccess={refreshAttachments}
                  title="Job 811 Tickets"
                  canEdit={canEdit}
                  emptyMessage="No 811 tickets attached to this job"
                />
                </div>
                )}

                {/* Job Building Permits - Using reusable FileUpload component */}
                {canShowModule('job_building_permits') && (
                <div data-testid="job-building-permits-module">
                <FileUpload
                  files={(formData.permit_files || []).map(f => ({
                    ...f,
                    filename: f.filename,
                    uploaded_at: f.uploaded_at,
                  }))}
                  uploadEndpoint={`/api/jobs/${id}/permits`}
                  deleteEndpoint={`/api/jobs/${id}/permits`}
                  onUploadSuccess={refreshAttachments}
                  onDeleteSuccess={refreshAttachments}
                  title="Job Building Permits"
                  canEdit={canEdit}
                  emptyMessage="No building permits attached to this job"
                />
                </div>
                )}
              </>
            ) : (
              <div className="p-4 bg-amber-50 border border-dashed border-amber-300 rounded-lg text-center">
                <Paperclip className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="text-sm text-amber-700 font-medium">Files & Photos</p>
                <p className="text-xs text-amber-600 mt-1">
                  Click "Save & Add Files" below to save and enable file uploads.
                </p>
              </div>
            )}

            {/* Save Buttons */}
            {/* Time Spent Tile — friction #13 + #19 (Estimated vs Actual).
                Only shows on existing jobs (need a job_id to query). */}
            {!isNew && timeRollup && (
              <Card className="mt-6" data-testid="time-spent-module">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    Time Spent on Job
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="text-center p-3 rounded-lg bg-slate-50">
                      <p className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] leading-none">
                        {timeRollup.total_hours.toFixed(1)}h
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">Total Logged</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-emerald-50">
                      <p className="text-2xl font-bold text-emerald-700 font-['Barlow_Condensed'] leading-none">
                        {timeRollup.job_hours.toFixed(1)}h
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">On Job</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-blue-50">
                      <p className="text-2xl font-bold text-blue-700 font-['Barlow_Condensed'] leading-none">
                        {timeRollup.travel_hours.toFixed(1)}h
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">Travel</p>
                    </div>
                    <div className={`text-center p-3 rounded-lg ${
                      timeRollup.estimated_hours <= 0 ? 'bg-slate-50' :
                      timeRollup.variance_hours <= 0 ? 'bg-emerald-50' : 'bg-orange-50'
                    }`}>
                      <p className={`text-2xl font-bold font-['Barlow_Condensed'] leading-none ${
                        timeRollup.estimated_hours <= 0 ? 'text-slate-400' :
                        timeRollup.variance_hours <= 0 ? 'text-emerald-700' : 'text-orange-700'
                      }`}>
                        {timeRollup.estimated_hours > 0
                          ? `${timeRollup.variance_hours >= 0 ? '+' : ''}${timeRollup.variance_hours.toFixed(1)}h`
                          : '—'}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">
                        vs Est ({timeRollup.estimated_hours > 0 ? `${timeRollup.estimated_hours}h` : 'not set'})
                      </p>
                    </div>
                  </div>
                  {timeRollup.by_user.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
                      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Hours by employee</p>
                      {timeRollup.by_user.map(u => (
                        <div key={u.user_id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{u.user_name}</span>
                          <span className="text-slate-500">
                            <span className="font-medium text-slate-900">{u.hours.toFixed(1)}h</span> · {u.entries} entries
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {timeRollup.entry_count === 0 && (
                    <p className="text-center text-sm text-slate-400 py-2">No time entries yet for this job.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Field Reports Section - Only show for existing jobs */}
            {canShowModule('field_reports') && !isNew && (
              <Card className="mt-6" data-testid="field-reports-module">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-amber-500" />
                    Field Reports
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {jobReports.precons.length === 0 && jobReports.softDigs.length === 0 && jobReports.locates.length === 0 && jobReports.cseForms.length === 0 ? (
                    <div className="text-center py-6">
                      <FileCheck className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500 mb-4">No field reports for this job yet</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/precon?job=${id}`)}>
                          <Plus className="w-3 h-3 mr-1" /> PreCon
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/soft-dig?job=${id}`)}>
                          <Plus className="w-3 h-3 mr-1" /> Soft Dig
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/locate?job=${id}`)}>
                          <Plus className="w-3 h-3 mr-1" /> Locate
                        </Button>
                        {canSubmitCSE && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/confined-space?job=${id}`)}
                            data-testid="job-add-cse-btn-empty"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Confined Space
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <PdfStorageNotice />
                      {/* PreCon Reports */}
                      {jobReports.precons.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">PreCon Reports ({jobReports.precons.length})</h4>
                          <div className="space-y-2">
                            {jobReports.precons.map(report => (
                              <div key={report.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`job-report-row-precon-${report.id}`}>
                                <div className="flex items-center gap-3">
                                  <Badge className={
                                    report.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    report.status === 'submitted_pending_review' ? 'bg-blue-100 text-blue-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }>
                                    {report.status === 'completed' ? 'Approved' : 
                                     report.status === 'submitted_pending_review' ? 'Pending' : 'Draft'}
                                  </Badge>
                                  <span className="text-sm text-slate-600">{jobIdentifyingSummary}</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button type="button" variant="ghost" size="sm" data-testid={`job-report-open-precon-${report.id}`} onClick={() => navigate(`/precon/${report.id}${report.status === 'completed' ? '?view=true' : ''}`)}>
                                    {report.status === 'completed' ? 'View' : 'Edit'}
                                  </Button>
                                  {report.status === 'completed' && (
                                    <Button type="button" variant="ghost" size="sm" className="text-amber-600" onClick={() => navigate(`/precon/${report.id}/preview`)} title="Open PDF preview">
                                      PDF
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Soft Dig Reports */}
                      {jobReports.softDigs.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Soft Dig Reports ({jobReports.softDigs.length})</h4>
                          <div className="space-y-2">
                            {jobReports.softDigs.map(report => (
                              <div key={report.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`job-report-row-softdig-${report.id}`}>
                                <div className="flex items-center gap-3">
                                  <Badge className={
                                    report.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    report.status === 'submitted_pending_review' ? 'bg-blue-100 text-blue-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }>
                                    {report.status === 'completed' ? 'Approved' : 
                                     report.status === 'submitted_pending_review' ? 'Pending' : 'Draft'}
                                  </Badge>
                                  <span className="text-sm text-slate-600">{jobIdentifyingSummary}</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button type="button" variant="ghost" size="sm" data-testid={`job-report-open-softdig-${report.id}`} onClick={() => navigate(`/soft-dig/${report.id}${report.status === 'completed' ? '?view=true' : ''}`)}>
                                    {report.status === 'completed' ? 'View' : 'Edit'}
                                  </Button>
                                  {report.status === 'completed' && (
                                    <Button type="button" variant="ghost" size="sm" className="text-amber-600" onClick={() => navigate(`/soft-dig/${report.id}/preview`)} title="Open PDF preview">
                                      PDF
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Locate Reports */}
                      {jobReports.locates.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Locate Reports ({jobReports.locates.length})</h4>
                          <div className="space-y-2">
                            {jobReports.locates.map(report => (
                              <div key={report.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`job-report-row-locate-${report.id}`}>
                                <div className="flex items-center gap-3">
                                  <Badge className={
                                    report.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    report.status === 'submitted_pending_review' ? 'bg-blue-100 text-blue-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }>
                                    {report.status === 'completed' ? 'Approved' : 
                                     report.status === 'submitted_pending_review' ? 'Pending' : 'Draft'}
                                  </Badge>
                                  <span className="text-sm text-slate-600">{jobIdentifyingSummary}</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button type="button" variant="ghost" size="sm" data-testid={`job-report-open-locate-${report.id}`} onClick={() => navigate(`/locate/${report.id}${report.status === 'completed' ? '?view=true' : ''}`)}>
                                    {report.status === 'completed' ? 'View' : 'Edit'}
                                  </Button>
                                  {report.status === 'completed' && (
                                    <Button type="button" variant="ghost" size="sm" className="text-amber-600" onClick={() => navigate(`/locate/${report.id}/preview`)} title="Open PDF preview">
                                      PDF
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Confined Space Reports */}
                      {jobReports.cseForms.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Confined Space Reports ({jobReports.cseForms.length})</h4>
                          <div className="space-y-2">
                            {jobReports.cseForms.map(report => (
                              <div key={report.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`job-report-row-cse-${report.id}`}>
                                <div className="flex items-center gap-3">
                                  <Badge className={
                                    report.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    report.status === 'submitted_pending_review' ? 'bg-blue-100 text-blue-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }>
                                    {report.status === 'completed' ? 'Approved' : 
                                     report.status === 'submitted_pending_review' ? 'Pending' : 'Draft'}
                                  </Badge>
                                  <span className="text-sm text-slate-600">{jobIdentifyingSummary}</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button type="button" variant="ghost" size="sm" data-testid={`job-report-open-cse-${report.id}`} onClick={() => navigate(`/confined-space/${report.id}`)}>
                                    Edit
                                  </Button>
                                  {report.status === 'completed' && (
                                    <Button type="button" variant="ghost" size="sm" className="text-amber-600" onClick={() => navigate(`/confined-space/${report.id}/preview`)} title="Open PDF preview">
                                      PDF
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add More Reports */}
                      <div className="flex flex-wrap gap-2 pt-3 border-t">
                        <span className="text-sm text-slate-500 self-center mr-2">Add report:</span>
                        <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/precon?job=${id}`)}>
                          <Plus className="w-3 h-3 mr-1" /> PreCon
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/soft-dig?job=${id}`)}>
                          <Plus className="w-3 h-3 mr-1" /> Soft Dig
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/locate?job=${id}`)}>
                          <Plus className="w-3 h-3 mr-1" /> Locate
                        </Button>
                        {canSubmitCSE && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/confined-space?job=${id}`)}
                            data-testid="job-add-cse-btn"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Confined Space
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {(canEdit || isNew) && (
              <div className="flex flex-col sm:flex-row gap-3">
                {isNew && (
                  <Button 
                    type="button"
                    onClick={(e) => handleSubmit(e, true)}
                    variant="outline"
                    className="flex-1 border-amber-500 text-amber-700 hover:bg-amber-50"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Paperclip className="w-4 h-4 mr-2" />
                        Save & Add Files
                      </>
                    )}
                  </Button>
                )}
                <Button 
                  type="submit" 
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                  disabled={saving}
                  data-testid="save-job-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isNew ? 'Create Job' : 'Save Changes'}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Delete Dialog
          When the job (or this series day-doc) has attachments AND it's
          part of a daily series, give the user a 3-way choice:
            • Keep with job → files move to earliest sibling
            • Trash (default) → files moved to OneDrive _trash/ folder
            • Permanently delete → original hard-delete behavior
          When there are no files OR no series, show the simple confirm. */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              {fileCount > 0 ? (
                <>
                  This job has <strong>{fileCount} attachment{fileCount === 1 ? '' : 's'}</strong>
                  {isSeriesDay && ' on this day-doc'}.
                  {' '}What should happen to them?
                </>
              ) : (
                'Are you sure you want to delete this job? This action cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {fileCount > 0 && isSeriesDay ? (
            <div className="space-y-2 pt-2">
              <Button
                variant="outline"
                className="w-full justify-start text-left h-auto py-3 border-green-300 hover:bg-green-50"
                onClick={() => handleDelete('keep')}
                disabled={deleting}
                data-testid="delete-keep-files-btn"
              >
                <div>
                  <div className="font-semibold text-green-700">Keep files with the job</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Move all attachments to the earliest day in this series. Recommended for &quot;crew finished early&quot;.
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-left h-auto py-3 border-amber-300 hover:bg-amber-50"
                onClick={() => handleDelete('trash')}
                disabled={deleting}
                data-testid="delete-trash-files-btn"
              >
                <div>
                  <div className="font-semibold text-amber-700">Move files to Trash (30 days)</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Files leave the job but stay in OneDrive Trash for 30 days. Recoverable.
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-left h-auto py-3 border-red-300 hover:bg-red-50"
                onClick={() => handleDelete('purge')}
                disabled={deleting}
                data-testid="delete-purge-files-btn"
              >
                <div>
                  <div className="font-semibold text-red-700">Permanently delete files</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Files are wiped from OneDrive immediately. No undo.
                  </div>
                </div>
              </Button>
              <AlertDialogFooter className="pt-2">
                <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              </AlertDialogFooter>
            </div>
          ) : (
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(fileCount > 0 ? 'trash' : 'purge')}
                className="bg-red-600 hover:bg-red-700"
                disabled={deleting}
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Deleting...' : fileCount > 0 ? 'Delete (files → Trash)' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Job confirmation dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md" data-testid="duplicate-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">Duplicate Job</DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-slate-700">
              <span className="block">
                Duplicate files along with the job?
              </span>
              <span className="block text-sm text-slate-500">
                A new job number will be created. Files will be{' '}
                <strong className="text-slate-700">linked</strong> to the new job
                (same underlying file — deleting from one job removes the link
                on both).
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDuplicateDialog(false)}
              data-testid="duplicate-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => performDuplicate(false)}
              data-testid="duplicate-without-files-btn"
            >
              Without files
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              onClick={() => performDuplicate(true)}
              data-testid="duplicate-with-files-btn"
            >
              <Copy className="w-4 h-4 mr-1" /> Duplicate with files
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Day Dialog */}
      <Dialog open={showAddDayDialog} onOpenChange={(open) => {
        setShowAddDayDialog(open);
        if (!open) {
          setEditingDay(null);
          setNewDayData({ date: '', time: '', assigned_to: [], notes: '', service_type: '', assigned_line_items: [], day_title: '' });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDay ? `Edit Day ${editingDay.day_number + 1}` : 'Add Day to Job'}</DialogTitle>
            <DialogDescription>
              {editingDay 
                ? 'Update the date, time, workers, or notes for this day.'
                : 'Add another day session to this job. Each day can have different workers assigned.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Job reference */}
            {formData.job_number && (
              <div className="text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded border">
                Job: <span className="font-mono font-medium text-slate-700">{formData.job_number}</span>
                {formData.job_title && <span className="ml-2 text-slate-600">— {formData.job_title}</span>}
              </div>
            )}
            <div className="space-y-2">
              <Label>Day Title (optional)</Label>
              <Input
                value={newDayData.day_title}
                onChange={(e) => setNewDayData(prev => ({ ...prev, day_title: e.target.value }))}
                placeholder={formData.job_title || 'Custom title for this day...'}
              />
              <p className="text-xs text-slate-500">Leave blank to use the job title</p>
            </div>
            <div className="space-y-2">
              <Label>Proposal Service Type</Label>
              <Select
                value={newDayData.service_type || formData.service_type}
                onValueChange={(val) => setNewDayData(prev => ({ ...prev, service_type: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Same as job" />
                </SelectTrigger>
                <SelectContent>
                  {proposalTypes.filter(t => t.name !== 'Other').map(t => (
                    <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                  ))}
                  <SelectItem value="Other" className="italic">Other (specify in notes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newDayData.date}
                onChange={(e) => setNewDayData(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Time (optional)</Label>
              <TimeInput
                value={newDayData.time}
                onChange={(val) => setNewDayData(prev => ({ ...prev, time: val }))}
                placeholder="Leave blank to use job default"
              />
            </div>
            <div className="space-y-2">
              <Label>Assigned Workers</Label>
              <p className="text-xs text-slate-500 mb-2">Leave unchecked to use same workers as Day 1</p>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-2">
                {team.filter(m => m.active).map(member => (
                  <div key={member.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`day-team-${member.id}`}
                      checked={newDayData.assigned_to.includes(member.id)}
                      onCheckedChange={(checked) => {
                        setNewDayData(prev => ({
                          ...prev,
                          assigned_to: checked
                            ? [...prev.assigned_to, member.id]
                            : prev.assigned_to.filter(id => id !== member.id)
                        }));
                      }}
                    />
                    <Label htmlFor={`day-team-${member.id}`} className="text-sm cursor-pointer">
                      {member.full_name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={newDayData.notes}
                onChange={(e) => setNewDayData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notes specific to this day..."
                rows={2}
              />
            </div>
            {(formData.service_line_items?.length > 0 || formData.line_items?.length > 0) && (
              <div className="space-y-2">
                <Label>Tasks for this day</Label>
                <p className="text-xs text-slate-500">Select which service line items are scheduled for this day</p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                  {(formData.service_line_items || formData.line_items || []).map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`line-item-${idx}`}
                        checked={newDayData.assigned_line_items.includes(idx)}
                        onChange={(e) => {
                          setNewDayData(prev => ({
                            ...prev,
                            assigned_line_items: e.target.checked
                              ? [...prev.assigned_line_items, idx]
                              : prev.assigned_line_items.filter(i => i !== idx)
                          }));
                        }}
                        className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                      />
                      <label htmlFor={`line-item-${idx}`} className="text-sm text-slate-700 cursor-pointer">
                        {item.service_name || item.service || item.name || `Item ${idx + 1}`}
                        {item.description && item.description !== '—' && item.description !== '' && (
                          <span className="text-slate-400 ml-1">— {item.description}</span>
                        )}
                        {item.quantity > 1 && (
                          <span className="text-xs text-slate-400 ml-1">(qty: {item.quantity})</span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDayDialog(false)}>Cancel</Button>
            <Button onClick={handleAddJobDay} className="bg-amber-500 hover:bg-amber-400 text-slate-900">
              {editingDay ? 'Save Changes' : 'Add Day'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* + Add Day and Change-date are now inline Popovers anchored to
          their trigger buttons in the day-strip — no Dialog/portal lag.
          See DayChipPopover above and the inline Popovers in the strip. */}
    </div>
  );
}
